import React from 'react';
import { AlertTriangle, Trash2 } from 'lucide-react';
import { Button } from '../../components/Button';
import { Modal } from '../../components/Modal';
import type { User as FirebaseUser } from 'firebase/auth';
import { deleteAccount } from '../../services/auth';
import { useToast } from '../../store/ToastContext';

interface SettingsDangerZoneProps {
    user: FirebaseUser | null | undefined;
}

export const SettingsDangerZone: React.FC<SettingsDangerZoneProps> = ({ user }) => {
    const [isDeleteDialogOpen, React_setIsDeleteDialogOpen] = React.useState(false);
    const [isDeleting, React_setIsDeleting] = React.useState(false);
    const { showToast } = useToast();

    if (!user || user.isAnonymous) {
        return null;
    }

    const handleDeleteAccount = async () => {
        React_setIsDeleting(true);
        try {
            await deleteAccount(user);
            showToast('Account deleted successfully.', 'success');
            React_setIsDeleteDialogOpen(false);
        } catch (err: unknown) {
            console.error('Failed to delete account:', err);
            if (err && typeof err === 'object' && 'code' in err && err.code === 'auth/requires-recent-login') {
                showToast('For security, please log out and log back in before deleting your account.', 'error');
            } else {
                showToast('Failed to delete account.', 'error');
            }
        } finally {
            React_setIsDeleting(false);
        }
    };

    return (
        <div className="bg-red-500/5">
            <div className="p-4">
                <h3 className="text-sm font-bold text-red-600 dark:text-red-400 mb-2">Danger Zone</h3>
                <p className="text-xs text-text-muted mb-4">
                    Once you delete your account, there is no going back. Please be certain.
                </p>
                <Button
                    variant="outline"
                    className="w-full sm:w-auto border-red-200 text-red-600 hover:bg-red-50 dark:border-red-900/50 dark:text-red-400 dark:hover:bg-red-950/30 flex items-center justify-center gap-2"
                    onClick={() => React_setIsDeleteDialogOpen(true)}
                >
                    <Trash2 size={16} />
                    Delete Account
                </Button>
            </div>

            <Modal
                isOpen={isDeleteDialogOpen}
                onClose={() => !isDeleting && React_setIsDeleteDialogOpen(false)}
                title="Delete Account"
            >
                <div className="space-y-4">
                    <div className="flex items-start gap-3 p-3 rounded-xl border border-red-500/30 bg-red-500/10">
                        <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
                        <p className="text-sm font-medium text-red-800 dark:text-red-300">
                            Warning: This action cannot be undone. Your user profile will be permanently deleted.
                        </p>
                    </div>
                    <p className="text-sm text-text-muted">
                        Are you absolutely sure you want to delete your account? You will lose access to all your tabs and groups.
                    </p>
                    <div className="flex justify-end gap-2 mt-4">
                        <Button 
                            variant="ghost" 
                            onClick={() => React_setIsDeleteDialogOpen(false)}
                            disabled={isDeleting}
                        >
                            Cancel
                        </Button>
                        <Button 
                            variant="primary" 
                            className="bg-red-600 hover:bg-red-700 text-white" 
                            onClick={handleDeleteAccount}
                            disabled={isDeleting}
                        >
                            {isDeleting ? 'Deleting...' : 'Delete Account'}
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};
